import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChartBar, SpinnerGap, ArrowCounterClockwise, Info, MagnifyingGlass } from "@phosphor-icons/react";
import { PURPOSES, FALLBACK_MODEL_REGISTRY, LOADING_MESSAGES, TEXT_DENSITIES, getStructurePlaceholder } from "@/lib/graphicsConstants";
import { FieldLabel } from "@/components/graphics/GraphicsHelpers";
import { ModelSelector } from "@/components/graphics/ModelSelector";
import { ColorPaletteSection } from "@/components/graphics/ColorPaletteSection";
import { StyleRatioSection } from "@/components/graphics/StyleRatioSection";
import { GraphicsPreview } from "@/components/graphics/GraphicsPreview";

export default function GraphicGeneratorPage() {
  const { api } = useAuth();

  // Form state
  const [topic, setTopic] = useState("");
  const [contentStructure, setContentStructure] = useState("");
  const [purpose, setPurpose] = useState("Infographic");
  const [industry, setIndustry] = useState("");
  const [context, setContext] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [primaryColor, setPrimaryColor] = useState("#007bff");
  const [textColor, setTextColor] = useState("#1a1a2e");
  const [accentColor, setAccentColor] = useState("#28a745");
  const [useAccent, setUseAccent] = useState(false);
  const [style, setStyle] = useState("Flat Design");
  const [ratio, setRatio] = useState("1:1");
  const [textDensity, setTextDensity] = useState("medium");
  const [includeBrandName, setIncludeBrandName] = useState(false);
  const [brandName, setBrandName] = useState("");

  // Provider/model state
  const [modelRegistry, setModelRegistry] = useState(FALLBACK_MODEL_REGISTRY);
  const [selectedProvider, setSelectedProvider] = useState("openai");
  const [selectedModel, setSelectedModel] = useState("gpt-image-1");

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [genId, setGenId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [showEnhanceMenu, setShowEnhanceMenu] = useState(false);

  // Edit state
  const [editInstruction, setEditInstruction] = useState("");
  const [editing, setEditing] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);

  // Research state
  const [researching, setResearching] = useState(false);

  // Themes state
  const [themes, setThemes] = useState([]);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [newThemeName, setNewThemeName] = useState("");
  const [savingTheme, setSavingTheme] = useState(false);

  // Brand state
  const [brandPalette, setBrandPalette] = useState(null);
  const [brandThemes, setBrandThemes] = useState([]);

  // Loading messages rotation
  useEffect(() => {
    if (!generating && !enhancing && !editing) return;
    const msgs = enhancing
      ? ["Enhancing visual quality...", "Refining details...", "Sharpening elements...", "Almost there..."]
      : editing
        ? ["Applying your edit...", "Modifying the graphic...", "Preserving layout...", "Rendering changes..."]
        : LOADING_MESSAGES;
    let i = 0;
    setLoadingMsg(msgs[0]);
    const interval = setInterval(() => {
      i = Math.min(i + 1, msgs.length - 1);
      setLoadingMsg(msgs[i]);
    }, 5000);
    return () => clearInterval(interval);
  }, [generating, enhancing, editing]);

  // Load model registry + themes on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const resp = await api.get("/graphics/models");
        if (resp.data.models) setModelRegistry(resp.data.models);
      } catch (err) { console.warn("Failed to load graphics models:", err); }
    };
    const loadThemes = async () => {
      try {
        const resp = await api.get("/graphics/themes");
        setThemes(resp.data.themes || []);
      } catch (err) { console.warn("Failed to load graphics themes:", err); }
    };
    loadModels();
    loadThemes();
  }, [api]);

  // Auto-pull brand palette
  useEffect(() => {
    const loadBrandPalette = async () => {
      try {
        const resp = await api.get("/profile/brand-palette");
        const palette = resp.data.brand_palette;
        if (palette) {
          setBrandPalette(palette);
          setBackgroundColor(palette.background_color || "#FFFFFF");
          setPrimaryColor(palette.primary_color || "#007bff");
          setTextColor(palette.text_color || "#1a1a2e");
          setAccentColor(palette.accent_color || "#28a745");
          setUseAccent(palette.use_accent || false);
        }
        if (resp.data.brand_themes?.length > 0) setBrandThemes(resp.data.brand_themes);
        // Auto-populate brand name from profile
        if (resp.data.business_name) setBrandName(resp.data.business_name);
      } catch (err) { console.warn("Failed to load brand palette:", err); }
    };
    loadBrandPalette();
  }, [api]);

  const fetchImageBlob = useCallback(async (id) => {
    try {
      const resp = await api.get(`/graphics/image/${id}`, { responseType: "blob" });
      return URL.createObjectURL(resp.data);
    } catch {
      toast.error("Failed to load generated image");
      return null;
    }
  }, [api]);

  const handleModelSelect = (combinedValue) => {
    const [prov, ...modelParts] = combinedValue.split(":");
    setSelectedProvider(prov);
    setSelectedModel(modelParts.join(":"));
  };

  const handleGenerate = async () => {
    if (!topic.trim() || topic.trim().length < 5) { toast.error("Topic must be at least 5 characters"); return; }
    setGenerating(true); setGenId(null); setImageUrl(null); setSaved(false); setIsEnhanced(false); setShowEditPanel(false);
    try {
      const resp = await api.post("/graphics/generate", {
        topic: topic.trim(), content_structure: contentStructure.trim(), graphic_purpose: purpose,
        industry_niche: industry.trim(), context_keywords: context.trim(),
        background_color: backgroundColor, primary_color: primaryColor, text_color: textColor,
        accent_color: accentColor, use_accent: useAccent, visual_style: style, aspect_ratio: ratio,
        provider: selectedProvider, model: selectedModel,
        text_density: textDensity, include_brand_name: includeBrandName, brand_name: brandName.trim(),
      });
      if (resp.data.gen_id) {
        setGenId(resp.data.gen_id);
        const url = await fetchImageBlob(resp.data.gen_id);
        setImageUrl(url);
        toast.success(`Visual generated via ${resp.data.model_label || selectedModel}!`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const handleEnhance = async (focus) => {
    if (!genId) return;
    setEnhancing(true); setShowEnhanceMenu(false);
    try {
      const resp = await api.post("/graphics/enhance", { gen_id: genId, focus, provider: selectedProvider, model: selectedModel });
      if (resp.data.gen_id) {
        setGenId(resp.data.gen_id);
        const url = await fetchImageBlob(resp.data.gen_id);
        setImageUrl(url); setIsEnhanced(true); setSaved(false);
        toast.success("Enhanced! Quality improved.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Enhancement failed"); }
    finally { setEnhancing(false); }
  };

  const handleEdit = async () => {
    if (!genId || !editInstruction.trim() || editInstruction.trim().length < 5) { toast.error("Edit instruction must be at least 5 characters"); return; }
    setEditing(true);
    try {
      const resp = await api.post("/graphics/edit", { gen_id: genId, instruction: editInstruction.trim(), provider: selectedProvider, model: selectedModel });
      if (resp.data.gen_id) {
        setGenId(resp.data.gen_id);
        const url = await fetchImageBlob(resp.data.gen_id);
        setImageUrl(url); setSaved(false); setEditInstruction("");
        toast.success("Edit applied!");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Edit failed"); }
    finally { setEditing(false); }
  };

  const handleResearch = async () => {
    if (!topic.trim() || topic.trim().length < 5) { toast.error("Enter a topic first (at least 5 characters)"); return; }
    setResearching(true);
    try {
      const resp = await api.post("/graphics/research", { topic: topic.trim(), graphic_purpose: purpose, industry_niche: industry.trim() });
      if (resp.data.research) { setContext(resp.data.research.slice(0, 2000)); toast.success("Research loaded into context!"); }
    } catch (e) { toast.error(e.response?.data?.detail || "Research failed"); }
    finally { setResearching(false); }
  };

  const handleSave = async () => {
    if (!genId) return;
    setSaving(true);
    try {
      await api.post("/graphics/save", {
        image_id: genId, topic, graphic_purpose: purpose, industry_niche: industry,
        context_keywords: context, content_structure: contentStructure, visual_style: style,
        aspect_ratio: ratio, background_color: backgroundColor, primary_color: primaryColor,
        text_color: textColor, accent_color: accentColor,
      });
      setSaved(true); toast.success("Saved to Image Library!");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleClear = () => {
    setTopic(""); setContentStructure(""); setPurpose("Infographic"); setIndustry(""); setContext("");
    setBackgroundColor("#FFFFFF"); setPrimaryColor("#007bff"); setTextColor("#1a1a2e");
    setAccentColor("#28a745"); setUseAccent(false); setStyle("Flat Design"); setRatio("1:1");
    setGenId(null); setImageUrl(null); setSaved(false); setIsEnhanced(false);
    setShowEditPanel(false); setEditInstruction(""); setSelectedProvider("openai"); setSelectedModel("gpt-image-1");
    setTextDensity("medium"); setIncludeBrandName(false);
  };

  const handleSaveTheme = async () => {
    if (!newThemeName.trim()) { toast.error("Enter a theme name"); return; }
    setSavingTheme(true);
    try {
      const resp = await api.post("/graphics/themes", {
        name: newThemeName.trim(), background_color: backgroundColor, primary_color: primaryColor,
        text_color: textColor, accent_color: accentColor, use_accent: useAccent,
      });
      setThemes(prev => [resp.data, ...prev]); setNewThemeName(""); toast.success("Theme saved!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save theme"); }
    finally { setSavingTheme(false); }
  };

  const handleLoadTheme = (theme) => {
    setBackgroundColor(theme.background_color); setPrimaryColor(theme.primary_color);
    setTextColor(theme.text_color); setAccentColor(theme.accent_color);
    setUseAccent(theme.use_accent); setShowThemePanel(false);
    toast.success(`Loaded "${theme.name}" theme`);
  };

  const handleDeleteTheme = async (themeId) => {
    try { await api.delete(`/graphics/themes/${themeId}`); setThemes(prev => prev.filter(t => t.theme_id !== themeId)); toast.success("Theme deleted"); }
    catch { toast.error("Failed to delete theme"); }
  };

  const isBusy = generating || enhancing || editing;
  const currentModelLabel = modelRegistry[selectedProvider]?.[selectedModel]?.label || selectedModel;

  return (
    <>
      <Header />
      <NoBusinessGuard>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          {/* Hero */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
                <ChartBar size={22} weight="duotone" className="text-violet-400" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="graphic-gen-title">
                AI Visual Content Generator
              </h1>
            </div>
            <p className="text-sm text-[var(--text-muted)] max-w-2xl">
              Create infographics, listicle images, step-by-step visuals, comparison charts, and data graphics — ready to share.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Form */}
            <div className="lg:col-span-3 space-y-5">
              <ModelSelector modelRegistry={modelRegistry} selectedProvider={selectedProvider} selectedModel={selectedModel} currentModelLabel={currentModelLabel} onModelSelect={handleModelSelect} />

              {/* Topic */}
              <div>
                <FieldLabel required>What is this visual about?</FieldLabel>
                <Textarea value={topic} onChange={e => setTopic(e.target.value.slice(0, 500))}
                  placeholder="e.g., '5 Steps to Double Your Client Retention' or '2025 Content Marketing Stats Every Business Needs'"
                  rows={2} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-none" data-testid="gfx-topic" />
                <p className="text-[10px] text-[var(--text-muted)] mt-1 text-right">{topic.length}/500</p>
              </div>

              {/* Purpose + Industry */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel required>Visual Type</FieldLabel>
                  <Select value={purpose} onValueChange={setPurpose}>
                    <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="gfx-purpose"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PURPOSES.map(p => (
                        <SelectItem key={p.key} value={p.key}>
                          <span className="font-medium">{p.label}</span>
                          <span className="text-[var(--text-muted)] ml-1.5 text-[10px]">— {p.desc}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel>
                    Industry / Niche
                    <span className="relative group ml-1 inline-block">
                      <Info size={12} className="text-[var(--text-muted)] cursor-help" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-[var(--bg-base)] border border-[var(--border)] rounded text-[10px] text-[var(--text-muted)] w-48 hidden group-hover:block z-10">
                        Dramatically improves visual metaphors and iconography choices.
                      </span>
                    </span>
                  </FieldLabel>
                  <Input value={industry} onChange={e => setIndustry(e.target.value.slice(0, 100))}
                    placeholder="e.g., Marketing, SaaS, Real Estate, Legal..."
                    className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="gfx-industry" />
                </div>
              </div>

              {/* Content Structure */}
              <div>
                <FieldLabel required>
                  Content to Visualize
                  <span className="normal-case font-normal text-[var(--text-muted)] ml-1">— the actual items, stats, or steps</span>
                </FieldLabel>
                <Textarea value={contentStructure} onChange={e => setContentStructure(e.target.value.slice(0, 1000))}
                  placeholder={getStructurePlaceholder(purpose)}
                  rows={6} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y font-mono" data-testid="gfx-content-structure" />
                <p className="text-[10px] text-[var(--text-muted)] mt-1 flex justify-between">
                  <span>Type the exact items you want shown in the graphic</span>
                  <span>{contentStructure.length}/1000</span>
                </p>
              </div>

              {/* Text Density + Brand Name */}
              <div className="space-y-4">
                <div>
                  <FieldLabel>Text Amount on Graphic</FieldLabel>
                  <div className="grid grid-cols-3 gap-2" data-testid="gfx-text-density">
                    {TEXT_DENSITIES.map(d => (
                      <button key={d.key} type="button" onClick={() => setTextDensity(d.key)}
                        data-testid={`gfx-density-${d.key}`}
                        className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border text-center transition-all ${textDensity === d.key
                          ? "border-violet-500 bg-violet-500/10 text-violet-300 ring-1 ring-violet-500/30"
                          : "border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"}`}>
                        <span className="text-sm font-semibold">{d.label}</span>
                        <span className="text-[10px] text-[var(--text-muted)] leading-tight">{d.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <Switch checked={includeBrandName} onCheckedChange={setIncludeBrandName}
                      id="brand-name-toggle" data-testid="gfx-brand-name-toggle"
                      className="data-[state=checked]:bg-violet-600" />
                    <label htmlFor="brand-name-toggle" className="text-sm text-[var(--text-secondary)] cursor-pointer select-none">
                      Include brand name on graphic
                    </label>
                  </div>
                  {includeBrandName && (
                    <Input value={brandName} onChange={e => setBrandName(e.target.value.slice(0, 50))}
                      placeholder="Brand name..." className="w-40 h-8 bg-[var(--bg-base)] border-[var(--border-strong)] text-xs"
                      data-testid="gfx-brand-name-input" />
                  )}
                </div>
              </div>

              {/* Context with Research */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <FieldLabel>Additional Context</FieldLabel>
                  {topic.trim().length >= 5 && (
                    <Button onClick={handleResearch} disabled={researching} variant="ghost"
                      className="h-10 sm:h-7 px-2.5 text-[10px] font-semibold text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 rounded-lg gap-1" data-testid="gfx-research-btn">
                      {researching ? <><SpinnerGap size={12} className="animate-spin" /> Researching...</> : <><MagnifyingGlass size={12} weight="bold" /> Research Topic</>}
                    </Button>
                  )}
                </div>
                <Textarea value={context} onChange={e => setContext(e.target.value.slice(0, 2000))}
                  placeholder="Supporting visual direction, data points, or auto-filled research context..."
                  rows={3} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y" data-testid="gfx-context" />
                <p className="text-[10px] text-[var(--text-muted)] mt-1 text-right">{context.length}/2000</p>
              </div>

              <ColorPaletteSection
                brandPalette={brandPalette} brandThemes={brandThemes} themes={themes}
                backgroundColor={backgroundColor} setBackgroundColor={setBackgroundColor}
                primaryColor={primaryColor} setPrimaryColor={setPrimaryColor}
                textColor={textColor} setTextColor={setTextColor}
                accentColor={accentColor} setAccentColor={setAccentColor}
                useAccent={useAccent} setUseAccent={setUseAccent}
                showThemePanel={showThemePanel} setShowThemePanel={setShowThemePanel}
                newThemeName={newThemeName} setNewThemeName={setNewThemeName}
                savingTheme={savingTheme} onSaveTheme={handleSaveTheme}
                onLoadTheme={handleLoadTheme} onDeleteTheme={handleDeleteTheme}
              />

              <StyleRatioSection style={style} setStyle={setStyle} ratio={ratio} setRatio={setRatio} />

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handleGenerate} disabled={isBusy || topic.trim().length < 5}
                  className="h-11 px-8 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl transition-all flex-1" data-testid="gfx-generate-btn">
                  {generating ? <><SpinnerGap size={16} className="animate-spin mr-2" />{loadingMsg}</> : <><ChartBar size={18} className="mr-2" />Generate Visual</>}
                </Button>
                <Button onClick={handleClear} variant="outline" className="h-11 px-6 text-sm rounded-xl" data-testid="gfx-clear-btn">
                  <ArrowCounterClockwise size={16} className="mr-2" />Clear All
                </Button>
              </div>
            </div>

            {/* Right: Preview */}
            <GraphicsPreview
              ratio={ratio} isBusy={isBusy} generating={generating} enhancing={enhancing}
              editing={editing} loadingMsg={loadingMsg} selectedProvider={selectedProvider}
              currentModelLabel={currentModelLabel} imageUrl={imageUrl} topic={topic}
              isEnhanced={isEnhanced} genId={genId} saving={saving} saved={saved}
              showEnhanceMenu={showEnhanceMenu} setShowEnhanceMenu={setShowEnhanceMenu}
              showEditPanel={showEditPanel} setShowEditPanel={setShowEditPanel}
              editInstruction={editInstruction} setEditInstruction={setEditInstruction}
              onSave={handleSave} onGenerate={handleGenerate} onEnhance={handleEnhance} onEdit={handleEdit}
            />
          </div>
        </main>
      </NoBusinessGuard>
    </>
  );
}
